const PREFIX = 'kinta.snap.';

/**
 * Last known profile, workspaces, accounts and categories, so the app can start and take quick-add entries with no
 * connection. Read only as a starting value (the query still refetches). Removed on every sign-out.
 */
export function readSnapshot<T>(name: string): T | undefined {
  try {
    const raw = localStorage.getItem(PREFIX + name);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

export function writeSnapshot(name: string, data: unknown): void {
  try {
    localStorage.setItem(PREFIX + name, JSON.stringify(data));
  } catch {
    // Quota or blocked storage: offline start-up is a convenience, not a requirement.
  }
}

export function clearSnapshots(): void {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Query options that start from the snapshot (marked stale, so it still refetches) and refresh it on success. */
export function withSnapshot<T>(name: string, fetcher: () => Promise<T>) {
  return {
    queryFn: async () => {
      const data = await fetcher();
      writeSnapshot(name, data);
      return data;
    },
    initialData: () => readSnapshot<T>(name),
    initialDataUpdatedAt: 0,
  };
}
